"use client";

import { useState, useEffect, FormEvent } from "react";
import { useRouter } from "next/navigation";
import { User, Lock, Eye, EyeOff } from "lucide-react";
import Swal from "sweetalert2";
import { FitoLogo } from "@/components/brand/fito-logo";
import { clearSessionTraces } from "@/store/auth-store";

const SAVED_ID_KEY = "fito.savedUserId";

export default function LoginPage() {
  const router = useRouter();
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [saveId, setSaveId] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(SAVED_ID_KEY);
    if (saved) {
      setUserId(saved);
      setSaveId(true);
    }
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!userId || !password) {
      Swal.fire({ icon: "warning", title: "아이디와 비밀번호를 입력하세요." });
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, password }),
      });

      const data = await res.json();

      if (data.success) {
        if (saveId) localStorage.setItem(SAVED_ID_KEY, userId);
        else localStorage.removeItem(SAVED_ID_KEY);
        // 앞사람이 쓰던 메뉴(권한) 캐시와 열어 둔 탭을 먼저 비운다 (2026-10-14 요건).
        // 같은 PC 에서 계정을 바꿔 들어올 때 앞사람 메뉴가 섞여 보이던 것을 막는다.
        clearSessionTraces();
        /**
         * 들어갈 화면 (2026-10-07 요건).
         *
         * 로그인하면 **규정대로의 첫 화면**으로 간다 — 로그아웃 전에 보던 화면으로
         * 돌아가지 않는다. 미들웨어가 붙여 준 next 는 "주소를 콕 집어 들어오다가
         * 로그인 벽에 막힌 경우"(발주서 QR → 입고창처럼 ?가 붙은 주소)에만 따른다.
         * 화면 주소만 달랑 들어 있는 next 는 직전에 보던 화면이라 버린다.
         */
        const next = new URLSearchParams(window.location.search).get("next") || "";
        const isInternal = next.startsWith("/") && !next.startsWith("//");
        const isDeepLink = next.includes("?");   // 바코드·objId 처럼 집어 든 주소
        const safeNext = isInternal && isDeepLink ? next : "";
        router.push(safeNext || String(data.landing || "/home"));
      } else {
        Swal.fire({
          icon: "error",
          title: "로그인 실패",
          text: data.message || "아이디 또는 비밀번호를 확인하세요.",
        });
      }
    } catch {
      Swal.fire({ icon: "error", title: "서버 오류", text: "잠시 후 다시 시도하세요." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="text-center mb-9">
          <FitoLogo className="mx-auto" height={64} />
          <p className="mt-3 text-[13px] font-normal text-muted-foreground tracking-wide">업무관리 시스템</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="login-input">
            <User size={16} className="login-input-icon" />
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="사용자 ID"
              autoFocus
              autoComplete="username"
            />
          </div>

          <div className="login-input">
            <Lock size={16} className="login-input-icon" />
            <input
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호"
              autoComplete="current-password"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPwd((v) => !v)}
              className="login-input-suffix"
              aria-label={showPwd ? "비밀번호 숨기기" : "비밀번호 보기"}
            >
              {showPwd ? <Eye size={16} /> : <EyeOff size={16} />}
            </button>
          </div>

          <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              checked={saveId}
              onChange={(e) => setSaveId(e.target.checked)}
              className="h-4 w-4 rounded border-input text-primary focus:ring-ring/40"
            />
            ID 저장
          </label>

          <button
            type="submit"
            disabled={loading}
            className="login-submit"
          >
            {loading ? "로그인 중..." : "로그인"}
          </button>
        </form>
      </div>

      <div className="login-footer">
        <div className="login-footer-left">
          <FitoLogo height={32} />
        </div>
        <div className="login-footer-info">
          <p>본사 : 충남 아산시 음봉면 4산단로 131 (우: 31418)</p>
          <p>TEL : 041-583-5900 &nbsp;|&nbsp; FAX : 041-583-5902</p>
          <p className="mt-1.5">COPYRIGHT © {new Date().getFullYear()} (주)피토 ALL RIGHTS RESERVED.</p>
        </div>
      </div>
    </div>
  );
}
